import { useState, useEffect } from 'react';
import { collection, getDocs, doc, updateDoc, addDoc, deleteDoc, serverTimestamp, increment } from 'firebase/firestore';
import { db } from '../firebaseConfig';
import { GlobalCategory } from '../types/labelSchema';
import { fuzzySearch, generateSearchTags } from '../utils/fuzzySearch';

export function useGlobalCategories() {
  const [categories, setCategories] = useState<GlobalCategory[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let mounted = true;
    
    const loadCategories = async () => {
      try {
        setLoading(true);
        setError(null);
        
        console.log('Loading global categories...');
        
        // Use getDocs instead of onSnapshot to avoid the internal assertion error
        const snapshot = await getDocs(collection(db, 'globalCategories'));
        
        console.log('Snapshot received:', snapshot);
        console.log('Number of docs:', snapshot.docs.length);
        
        if (mounted) {
          const categoriesData = snapshot.docs.map(doc => ({
            id: doc.id,
            ...doc.data()
          })) as GlobalCategory[];
          
          console.log('Categories data:', categoriesData);
          setCategories(categoriesData);
          setLoading(false);
        }
      } catch (err) {
        console.error('Error loading global categories:', err);
        console.error('Error details:', err);
        if (mounted) {
          setError(`Failed to load global categories: ${err instanceof Error ? err.message : String(err)}`);
          setLoading(false);
        }
      }
    };

    loadCategories();

    return () => {
      mounted = false;
    };
  }, []);

  const searchCategories = (searchTerm: string, maxResults = 20) => {
    return fuzzySearch(categories, searchTerm, { maxResults });
  };

  const getCategoryById = (id: string) => {
    return categories.find(cat => cat.id === id);
  };

  const incrementUsage = async (categoryId: string) => {
    try {
      await updateDoc(doc(db, 'globalCategories', categoryId), {
        usageCount: increment(1),
        updatedAt: serverTimestamp()
      });
    } catch (error) {
      console.error('Error incrementing usage count:', error);
    }
  };

  const updateCategory = async (categoryId: string, updateData: Partial<GlobalCategory>) => {
    try {
      await updateDoc(doc(db, 'globalCategories', categoryId), {
        ...updateData,
        updatedAt: serverTimestamp()
      });
      console.log('✅ Global category updated successfully');
    } catch (error) {
      console.error('Error updating global category:', error);
      throw error;
    }
  };

  const deleteCategory = async (categoryId: string) => {
    try {
      await deleteDoc(doc(db, 'globalCategories', categoryId));
      console.log('✅ Global category deleted successfully');
    } catch (error) {
      console.error('Error deleting global category:', error);
      throw error;
    }
  };

  return {
    categories,
    loading,
    error,
    searchCategories,
    getCategoryById,
    incrementUsage,
    updateCategory,
    deleteCategory
  };
}

export function useCreateGlobalCategory() {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const createCategory = async (categoryData: {
    name: string;
    displayName: string;
    description?: string;
    color: string;
    icon?: string;
    shapes: string[];
    hotkey?: string;
    category?: string; // NEW: top-level category (optional)
    subcategory: string; // RENAMED FROM category: subcategory (required)
    attributes?: string[];
  }) => {
    setLoading(true);
    setError(null);

    try {
      const tags = generateSearchTags(categoryData.name);
      
      const newCategory: Omit<GlobalCategory, 'id'> = {
        ...categoryData,
        attributes: categoryData.attributes || [],
        tags,
        createdAt: serverTimestamp(),
        createdBy: 'admin', // TODO: Get from auth context
        usageCount: 0,
        active: true
      };

      const docRef = await addDoc(collection(db, 'globalCategories'), newCategory);
      
      setLoading(false);
      return docRef.id;
    } catch (err) {
      console.error('Error creating global category:', err);
      setError('Failed to create global category');
      setLoading(false);
      throw err;
    }
  };

  return {
    createCategory,
    loading,
    error
  };
}
